import { FC, Suspense, useCallback, useMemo, useState } from "react"
import { safeLazy } from "@/helpers/safeLazy"
import { useCatalogs } from "@/hooks/useCatalogs"
import { useDevDebugVisible } from "@/hooks/useDevDebugVisible"
import {
  IItemListQuery,
  SORT_DEFAULT_KEY,
  useInventorySortSearch
} from "@/hooks/useInventorySortSearch"
import { InventoryWalletCounters } from "@/modals/constructor/inventory/InventoryModal/ui/InventoryWalletCounters"
import { useModals } from "@/modals/hooks"
import { HeroVitalBars } from "@/providers/HeroVitals"
import { useGetInventoryQuery } from "@/store/apis/inventoryAPI"
import { IInventoryItem } from "@/store/apis/inventoryAPI/inventory.types"
import { useGetMeQuery } from "@/store/apis/playerAPI"
import { Modal } from "@/ui/Modal"
import styles from "./InventoryModal.module.scss"
import { deriveInventoryTabsView } from "./helpers"
import { ConsumablesTab } from "./ui/ConsumablesTab"
import { EquipmentTab } from "./ui/EquipmentTab"
import { InventoryListPanel } from "./ui/InventoryListPanel"
import { IInventoryTab, TabBar } from "./ui/TabBar"

const DebugPanel = safeLazy(() =>
  import("@/modals/constructor/inventory/InventoryModal/ui/DebugPanel").then(
    (m) => ({ default: m.DebugPanel })
  )
)

const modalType = "inventory"

// Module-level constant — the query string cannot change without a reload.
const DEBUG_REQUESTED = new URLSearchParams(window.location.search).has("debug")

const EMPTY_ITEMS: IInventoryItem[] = []

const DEFAULT_QUERY: IItemListQuery = { sortKey: SORT_DEFAULT_KEY, search: "" }
const DEFAULT_QUERIES: Record<IInventoryTab, IItemListQuery> = {
  equipment: DEFAULT_QUERY,
  consumables: DEFAULT_QUERY,
  resources: DEFAULT_QUERY
}

export const InventoryModal: FC = () => {
  const { data } = useGetInventoryQuery()
  const { rarities, slotLayout } = useCatalogs()
  const { openModal } = useModals()
  const [activeTab, setActiveTab] = useState<IInventoryTab>("equipment")
  const [queries, setQueries] =
    useState<Record<IInventoryTab, IItemListQuery>>(DEFAULT_QUERIES)

  const { data: me } = useGetMeQuery()
  const debugEnabled = useDevDebugVisible(DEBUG_REQUESTED)

  const ctx = useInventorySortSearch(data?.items ?? EMPTY_ITEMS, rarities)

  const tabs = useMemo(
    () => (data ? deriveInventoryTabsView(data.items, slotLayout) : null),
    [data, slotLayout]
  )

  const layoutOrder = useMemo(
    () => slotLayout.map((s) => s.position),
    [slotLayout]
  )

  const openItem = useCallback(
    (item: IInventoryItem) =>
      openModal("inventoryItem", { instanceId: item.id }),
    [openModal]
  )

  const setTabQuery = useCallback(
    (tab: IInventoryTab, query: IItemListQuery) =>
      setQueries((prev) => ({ ...prev, [tab]: query })),
    []
  )

  return (
    <Modal.Fullscreen type={modalType}>
      <div className={styles.InventoryModal}>
        <TabBar active={activeTab} onChange={setActiveTab} />
        <HeroVitalBars />
        <InventoryWalletCounters />
        <div className={styles.InventoryModal_body}>
          {tabs && data && activeTab === "equipment" && (
            <EquipmentTab
              key={activeTab}
              view={tabs}
              layoutOrder={layoutOrder}
              weight={data.weight}
              query={queries.equipment}
              ctx={ctx}
              onQueryChange={(query) => setTabQuery("equipment", query)}
              onItemClick={openItem}
            />
          )}
          {tabs && data && activeTab === "consumables" && (
            <ConsumablesTab
              key={activeTab}
              quickSlots={data.quickSlots}
              items={tabs.consumables}
              weight={data.weight}
              query={queries.consumables}
              ctx={ctx}
              onQueryChange={(query) => setTabQuery("consumables", query)}
              onItemClick={openItem}
            />
          )}
          {tabs && data && activeTab === "resources" && (
            <InventoryListPanel
              key={activeTab}
              items={tabs.resources}
              weight={data.weight}
              query={queries.resources}
              ctx={ctx}
              onQueryChange={(query) => setTabQuery("resources", query)}
              onItemClick={openItem}
            />
          )}
          {debugEnabled && me?.id && data && (
            <Suspense fallback={null}>
              <DebugPanel playerId={me.id} items={data.items} />
            </Suspense>
          )}
        </div>
      </div>
    </Modal.Fullscreen>
  )
}
